import React, { useState, useEffect } from "react";
import { Box, Typography, useTheme, useMediaQuery, CircularProgress } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import BannerSection from "../../components/common/BannerSection";
import NewsLogoSection from "../HomePage/NewsLogoSection";
import Breadcrumb from "../../components/common/Breadcrumb/Breadcrumb";
import { Images } from "../../utils/Images";
import SubmitForm from "../../components/common/SubmitForm";
import { eightyGAction } from "../../store/slices/eightyGSlice";
import { downloadEightyGCertificate } from "../../store/slices/transactionSlice";
import {
  sanitizeFormFieldValue,
  validateIndianMobile,
} from "../../utils/phoneValidation";

const Download80GForm = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isBelow900 = useMediaQuery("(max-width: 899px)");
  const isAbove1200 = useMediaQuery("(min-width: 1200px)");
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { loading, success, error } = useSelector((state) => state.eightyG);

  // Get transaction ID and details from navigation state or URL query params (email link fallback)
  const searchParams = new URLSearchParams(location.search);
  const transactionIdFromState = location?.state?.transactionId || searchParams.get('transactionId') || "";
  const detailsFromState = location?.state?.details || {
    name: searchParams.get('name') || '',
    email: searchParams.get('email') || '',
    phone: searchParams.get('phone') || '',
  };

  const [formData, setFormData] = useState({
    transactionId: transactionIdFromState, // Payment ID (what backend expects)
    panNumber: "",
    nameAsPerPan: detailsFromState.name || detailsFromState.fullName || "",
    email: detailsFromState.email || "",
    phoneNumber: detailsFromState.phone || detailsFromState.phoneNumber || "",
    fullAddress: "",
  });

  // Update form data when location state changes
  useEffect(() => {
    if (transactionIdFromState) {
      setFormData((prev) => ({
        ...prev,
        transactionId: transactionIdFromState,
      }));
    }
    if (detailsFromState.email) {
      setFormData((prev) => ({
        ...prev,
        email: detailsFromState.email,
      }));
    }
    if (detailsFromState.name || detailsFromState.fullName) {
      setFormData((prev) => ({
        ...prev,
        nameAsPerPan: detailsFromState.name || detailsFromState.fullName || "",
      }));
    }
    if (detailsFromState.phone || detailsFromState.phoneNumber) {
      setFormData((prev) => ({
        ...prev,
        phoneNumber: detailsFromState.phone || detailsFromState.phoneNumber || "",
      }));
    }
  }, [transactionIdFromState, detailsFromState]);

  // Auto-fill form from user profile when user is logged in
  useEffect(() => {
    if (user && user.id) {
      // Only auto-fill if fields are empty and user has the data
      setFormData((prevForm) => ({
        ...prevForm,
        panNumber: prevForm.panNumber || user.pan || "",
        nameAsPerPan: prevForm.nameAsPerPan || user.name || "",
        email: prevForm.email || user.email || "",
        phoneNumber: prevForm.phoneNumber || user.phone || user.mobile || "",
        // Full Address is not available in user profile, so leave it empty
      }));
    }
  }, [user]);
  const [showPopup, setShowPopup] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    const sanitized = sanitizeFormFieldValue(field, value);
    setFormData((prev) => ({ ...prev, [field]: sanitized }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    const validationErrors = {};
    
    if (!formData.transactionId) {
      validationErrors.transactionId = "Transaction ID is required";
    }
    
    if (!formData.panNumber) {
      validationErrors.panNumber = "PAN Number is required";
    } else {
      // Validate PAN format: ABCDE1234F
      const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
      if (!panRegex.test(formData.panNumber.toUpperCase())) {
        validationErrors.panNumber = "PAN must be in format: ABCDE1234F";
      }
    }
    
    if (!formData.nameAsPerPan) {
      validationErrors.nameAsPerPan = "Name as per PAN is required";
    }
    
    if (!formData.email) {
      validationErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      validationErrors.email = "Invalid email address";
    }
    
    const phoneCheck = validateIndianMobile(formData.phoneNumber);
    if (!phoneCheck.isValid) {
      validationErrors.phoneNumber = phoneCheck.error;
    }
    
    if (!formData.fullAddress) {
      validationErrors.fullAddress = "Full Address is required";
    }
    
    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      // First update transaction status
      // Backend expects payment ID (like pay_xxx), not database ID
      // Use payment ID (transactionId) for the update endpoint
      if (formData.transactionId) {
        await dispatch(downloadEightyGCertificate(formData.transactionId)).unwrap();
      }

      // Then submit 80G form
      // Backend expects payment ID for 80G generation as well
      const transactionIdForAPI = formData.transactionId;
      
      await dispatch(eightyGAction({
        userId: user?.id,
        transactionId: transactionIdForAPI,
        details: {
          panNumber: formData.panNumber.toUpperCase(),
          panName: formData.nameAsPerPan,
          email: formData.email,
          phoneNumber: validateIndianMobile(formData.phoneNumber).digits,
          address: formData.fullAddress,
        }
      })).unwrap();

      setShowPopup(true);
    } catch (error) {
      console.error("80G Form submission failed:", error);
      alert(error || "Failed to submit 80G form. Please try again.");
    }
  };

  return (
    <Box>
      {/* ✅ Breadcrumb Section */}
      <Breadcrumb
        items={[
          { label: "My Profile", path: "/profile" },
          { label: "80G Form", path: null },
        ]}
      />

      {/* ✅ Header Section */}
      <Box sx={{ mb: isMobile ? "10px" : "40px", mt: isMobile ? "0" : "30px" }}>
        <BannerSection title="80G Form" subtitle="" />
      </Box>

      {/* ✅ Form Section */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "stretch",
          justifyContent: "center",
          bgcolor: isBelow900 ? "transparent" : "#FAFAFA",
          borderRadius: "20px",
          padding: isBelow900 ? "0" : isAbove1200 ? "30px" : "20px",
          py: { xs: 2, md: 4 },
          width: "94.44%",
          maxWidth: "1440px",
          margin: "0 auto",
          minHeight: "70vh",
          border: isBelow900 ? "none" : "1px solid #EDEDED",
          boxShadow: "none",
        }}
      >
        {/* Left Side - Info & Image */}
        {!isBelow900 && (
          <Box
            sx={{
              flex: 1,
              pr: { md: 6 },
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                mb: 2,
                color: "#1D1D1B",
                fontSize: isMobile ? "16px" : isAbove1200 ? "24px" : "18px",
                lineHeight: "normal",
                textAlign: "left",
              }}
            >
              Download / Request Form
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 400,
                color: "#5E5E5E",
                fontSize: isMobile ? "12px" : isAbove1200 ? "14px" : "12px",
                lineHeight: "normal",
                textAlign: "left",
                maxWidth: "435px",
                mb: 2,
              }}
            >
              As per Indian Government Regulations, please provide your PAN and
              complete address to obtain your 80G receipt.
            </Typography>
            <Box
              component="img"
              src={Images.requestForm} // replace with actual image
              alt="Document with Stamp"
              sx={{
                width: "100%",
                maxWidth: "420px",
                height: "auto",
                margin: "0 auto",
              }}
            />
          </Box>
        )}

        {/* Right Side - Form */}
        <Box
          sx={{
            flex: 1.6,
            bgcolor: "#fff",
            borderRadius: "16px",
            paddingX: isMobile ? "15px" : isAbove1200 ? "30px" : "20px",
            paddingY: isMobile ? "20px" : isAbove1200 ? "30px" : "30px",
            boxShadow: "0px 3px 7px 0px #00000017",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            position: "relative",
            border: isBelow900 ? "1px solid #EDEDED" : "none",
            marginBottom: isMobile ? "16px" : "26px",
          }}
        >
          <Typography
            variant="h5"
            sx={{
              color: "#1D1D1B",
              fontWeight: "700",
              fontSize: isMobile ? "14px" : isAbove1200 ? "22px" : "18px",
              paddingBottom: isMobile ? "10px" : isAbove1200 ? "20px" : "15px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <img
              src={Images.streamlineSharpStar}
              alt="star"
              style={{ width: 24, height: 24, marginRight: 8 }}
            />
            Request 80G Form
          </Typography>

          <Box sx={{ borderBottom: "1px solid #E8E8E8", mb: 3 }} />

          {/* Row 1 */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              marginBottom: isMobile ? "10px" : isAbove1200 ? "21px" : "15px",
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  color: "#141414",
                  fontWeight: "600",
                  fontSize: isMobile ? "13px" : isAbove1200 ? "16px" : "14px",
                  paddingBottom: isMobile ? "8px" : "12px",
                  display: "flex",
                  alignItems: "center",
                  gap: isMobile ? "16px" : isAbove1200 ? "21px" : "18px",
                }}
              >
                Transaction ID
              </Typography>
              <input
                type="text"
                placeholder="Enter Transaction ID"
                value={formData.transactionId}
                onChange={(e) => handleChange("transactionId", e.target.value)}
                style={{
                  width: "100%",
                  padding: isMobile ? "12px" : "16px",
                  borderRadius: isMobile ? "4px" : "8px",
                  border: errors.transactionId 
                    ? "1px solid #F04951" 
                    : isMobile ? "1px solid #E8E8E8" : "1px solid #DDE2E5",
                  outline: "none",
                  fontSize: isMobile ? "12px" : "14px",
                  color: "#5E5E5E",
                }}
              />
              {errors.transactionId && (
                <Typography sx={{ color: "#F04951", fontSize: "12px", mt: 0.5 }}>
                  {errors.transactionId}
                </Typography>
              )}
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  color: "#141414",
                  fontWeight: "600",
                  fontSize: isMobile ? "13px" : isAbove1200 ? "16px" : "14px",
                  paddingBottom: isMobile ? "8px" : "12px",
                  display: "flex",
                  alignItems: "center",
                  gap: isMobile ? "16px" : isAbove1200 ? "21px" : "18px",
                }}
              >
                PAN Number
              </Typography>
              <input
                type="text"
                placeholder="Enter PAN Number"
                value={formData.panNumber}
                onChange={(e) => handleChange("panNumber", e.target.value.toUpperCase())}
                maxLength={10}
                style={{
                  width: "100%",
                  padding: isMobile ? "12px" : "16px",
                  borderRadius: isMobile ? "4px" : "8px",
                  border: errors.panNumber 
                    ? "1px solid #F04951" 
                    : isMobile ? "1px solid #E8E8E8" : "1px solid #DDE2E5",
                  outline: "none",
                  fontSize: isMobile ? "12px" : "14px",
                  color: "#5E5E5E",
                }}
              />
              {errors.panNumber && (
                <Typography sx={{ color: "#F04951", fontSize: "12px", mt: 0.5 }}>
                  {errors.panNumber}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Row 2 */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              marginBottom: isMobile ? "10px" : isAbove1200 ? "21px" : "15px",
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  color: "#141414",
                  fontWeight: "600",
                  fontSize: isMobile ? "13px" : isAbove1200 ? "16px" : "14px",
                  paddingBottom: isMobile ? "8px" : "12px",
                  display: "flex",
                  alignItems: "center",
                  gap: isMobile ? "16px" : isAbove1200 ? "21px" : "18px",
                }}
              >
                Name as per PAN
              </Typography>
              <input
                type="text"
                placeholder="Enter Name as per PAN"
                value={formData.nameAsPerPan}
                onChange={(e) => handleChange("nameAsPerPan", e.target.value)}
                style={{
                  width: "100%",
                  padding: isMobile ? "12px" : "16px",
                  borderRadius: isMobile ? "4px" : "8px",
                  border: errors.nameAsPerPan 
                    ? "1px solid #F04951" 
                    : isMobile ? "1px solid #E8E8E8" : "1px solid #DDE2E5",
                  outline: "none",
                  fontSize: isMobile ? "12px" : "14px",
                  color: "#5E5E5E",
                }}
              />
              {errors.nameAsPerPan && (
                <Typography sx={{ color: "#F04951", fontSize: "12px", mt: 0.5 }}>
                  {errors.nameAsPerPan}
                </Typography>
              )}
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  color: "#141414",
                  fontWeight: "600",
                  fontSize: isMobile ? "13px" : isAbove1200 ? "16px" : "14px",
                  paddingBottom: isMobile ? "8px" : "12px",
                  display: "flex",
                  alignItems: "center",
                  gap: isMobile ? "16px" : isAbove1200 ? "21px" : "18px",
                }}
              >
                Email ID
              </Typography>
              <input
                type="email"
                placeholder="Enter Email ID"
                value={formData.email}
                onChange={(e) => handleChange("email", e.target.value)}
                style={{
                  width: "100%",
                  padding: isMobile ? "12px" : "16px",
                  borderRadius: isMobile ? "4px" : "8px",
                  border: errors.email 
                    ? "1px solid #F04951" 
                    : isMobile ? "1px solid #E8E8E8" : "1px solid #DDE2E5",
                  outline: "none",
                  fontSize: isMobile ? "12px" : "14px",
                  color: "#5E5E5E",
                }}
              />
              {errors.email && (
                <Typography sx={{ color: "#F04951", fontSize: "12px", mt: 0.5 }}>
                  {errors.email}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Row 3 */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              marginBottom: isMobile ? "10px" : isAbove1200 ? "21px" : "15px",
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  color: "#141414",
                  fontWeight: "600",
                  fontSize: isMobile ? "13px" : isAbove1200 ? "16px" : "14px",
                  paddingBottom: isMobile ? "8px" : "12px",
                  display: "flex",
                  alignItems: "center",
                  gap: isMobile ? "16px" : isAbove1200 ? "21px" : "18px",
                }}
              >
                Phone Number
              </Typography>
              <input
                type="tel"
                inputMode="numeric"
                maxLength={10}
                placeholder="Enter Phone Number"
                value={formData.phoneNumber}
                onChange={(e) => handleChange("phoneNumber", e.target.value)}
                style={{
                  width: "100%",
                  padding: isMobile ? "12px" : "16px",
                  borderRadius: isMobile ? "4px" : "8px",
                  border: errors.phoneNumber 
                    ? "1px solid #F04951" 
                    : isMobile ? "1px solid #E8E8E8" : "1px solid #DDE2E5",
                  outline: "none",
                  fontSize: isMobile ? "12px" : "14px",
                  color: "#5E5E5E",
                }}
              />
              {errors.phoneNumber && (
                <Typography sx={{ color: "#F04951", fontSize: "12px", mt: 0.5 }}>
                  {errors.phoneNumber}
                </Typography>
              )}
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  color: "#141414",
                  fontWeight: "600",
                  fontSize: isMobile ? "13px" : isAbove1200 ? "16px" : "14px",
                  paddingBottom: isMobile ? "8px" : "12px",
                  display: "flex",
                  alignItems: "center",
                  gap: isMobile ? "16px" : isAbove1200 ? "21px" : "18px",
                }}
              >
                Full Address
              </Typography>
              <input
                type="text"
                placeholder="Enter Full Address"
                value={formData.fullAddress}
                onChange={(e) => handleChange("fullAddress", e.target.value)}
                style={{
                  width: "100%",
                  padding: isMobile ? "12px" : "16px",
                  borderRadius: isMobile ? "4px" : "8px",
                  border: errors.fullAddress 
                    ? "1px solid #F04951" 
                    : isMobile ? "1px solid #E8E8E8" : "1px solid #DDE2E5",
                  outline: "none",
                  fontSize: isMobile ? "12px" : "14px",
                  color: "#5E5E5E",
                }}
              />
              {errors.fullAddress && (
                <Typography sx={{ color: "#F04951", fontSize: "12px", mt: 0.5 }}>
                  {errors.fullAddress}
                </Typography>
              )}
            </Box>
          </Box>
          <Typography
            variant="body2"
            sx={{
              color: "#4A4A49",
              fontWeight: "500",
              fontSize: isMobile ? "10px" : isAbove1200 ? "14px" : "12px",
              paddingBottom: isMobile ? "20px" : "20px",
            }}
          >
            Please verify your details especially <b>PAN Number</b> and{" "}
            <b>Full Name</b> to obtain the proper 80G Certificate.
          </Typography>

          {/* ✅ Submit Button */}
          <button
            onClick={handleSubmit}
            disabled={loading}
            style={{
              backgroundColor: loading ? "#d63f46" : "#F04951",
              color: "#FFFFFF",
              borderRadius: "10px",
              boxShadow: "0px 6px 0px #141414",
              height: isMobile ? "36px" : "44px",
              width: isMobile ? "100%" : "160px",
              fontFamily: "Red Hat Display, sans-serif",
              fontWeight: 700,
              fontSize: isMobile ? "12px" : "14px",
              lineHeight: isMobile ? "11px" : "14px",
              textAlign: "center",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: isMobile ? "4px" : "8px",
              textTransform: "none",
              transition: "all 0.3s ease",
              cursor: loading ? "not-allowed" : "pointer",
              border: "none",
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? (
              <>
                <CircularProgress size={16} sx={{ color: "#FFFFFF" }} />
                Processing...
              </>
            ) : (
              "Submit"
            )}
          </button>

          {/* ✅ Show Popup */}
          {showPopup && <SubmitForm onClose={() => {
            setShowPopup(false);
            navigate('/');
          }} />}

          {/* ✅ Decorative SVG */}
          {!isMobile && (
            <Box
              sx={{
                position: "absolute",
                bottom: 20,
                right: 0,
                zIndex: 10,
              }}
            >
              <img
                src={Images.stringHand}
                alt="string hand"
                style={{ width: 90, height: "auto" }}
              />
            </Box>
          )}
        </Box>
      </Box>

      {/* ✅ Logos Section */}
      <Box sx={{ mt: 4 }}>
        <NewsLogoSection />
      </Box>
    </Box>
  );
};

export default Download80GForm;
